// frontend/src/components/engineer/components/MonthlyStatsCard.tsx - PROFESSIONAL UI
import React from 'react';
import { 
  ArrowTrendingUpIcon,
  CheckBadgeIcon,
  ClockIcon,
  UserGroupIcon,
  FireIcon
} from '@heroicons/react/24/outline';
import { MonthlyStats } from '../../../services/dashboardStats.service';

interface MonthlyStatsCardProps {
  stats: MonthlyStats | null;
}

const MonthlyStatsCard: React.FC<MonthlyStatsCardProps> = ({ stats }) => {
  const currentMonth = new Date().toLocaleDateString('en-US', { month: 'long' });
  
  // Calculate approval rate
  const approvalRate = stats && stats.totalRequests > 0 
    ? Math.round((stats.approvedRequests / stats.totalRequests) * 100) 
    : 0;

  return (
    <div style={{
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
      borderRadius: '1rem',
      padding: '1.5rem',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
      color: 'white',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background Pattern */}
      <div style={{
        position: 'absolute',
        top: '-50px',
        right: '-50px',
        width: '200px',
        height: '200px',
        background: 'radial-gradient(circle, rgba(194, 158, 93, 0.15) 0%, transparent 70%)',
        borderRadius: '50%'
      }} />
      <div style={{
        position: 'absolute',
        bottom: '-30px',
        left: '-30px',
        width: '150px',
        height: '150px',
        background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)',
        borderRadius: '50%'
      }} />

      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1.25rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
            <div style={{
              width: '2rem',
              height: '2rem',
              background: 'linear-gradient(135deg, #c29e5d 0%, #d4b373 100%)',
              borderRadius: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <ArrowTrendingUpIcon style={{ width: '1.125rem', height: '1.125rem', color: 'white' }} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>
                {currentMonth}
              </h3>
              <p style={{ margin: 0, fontSize: '0.6875rem', opacity: 0.6 }}>
                Monthly Overview
              </p>
            </div>
          </div>
          
          {/* Approval Rate Badge */}
          <div style={{
            padding: '0.375rem 0.75rem',
            background: approvalRate >= 80 
              ? 'rgba(16, 185, 129, 0.2)' 
              : approvalRate >= 50 
                ? 'rgba(245, 158, 11, 0.2)' 
                : 'rgba(239, 68, 68, 0.2)',
            borderRadius: '1rem',
            border: `1px solid ${approvalRate >= 80 
              ? 'rgba(16, 185, 129, 0.3)' 
              : approvalRate >= 50 
                ? 'rgba(245, 158, 11, 0.3)' 
                : 'rgba(239, 68, 68, 0.3)'}`
          }}>
            <span style={{
              fontSize: '0.75rem',
              fontWeight: '600',
              color: approvalRate >= 80 ? '#10b981' : approvalRate >= 50 ? '#f59e0b' : '#ef4444'
            }}>
              {approvalRate}% approved
            </span>
          </div>
        </div>

        {/* Main Stats Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '1rem',
          marginBottom: '1rem'
        }}>
          {/* Approved */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '0.75rem',
            padding: '1rem',
            border: '1px solid rgba(255, 255, 255, 0.08)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <CheckBadgeIcon style={{ width: '1rem', height: '1rem', color: '#10b981' }} />
              <span style={{ fontSize: '0.6875rem', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Approved
              </span>
            </div>
            <p style={{ margin: 0, fontSize: '1.75rem', fontWeight: '700' }}>
              {stats?.approvedRequests || 0}
            </p>
          </div>

          {/* Total Hours */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '0.75rem',
            padding: '1rem',
            border: '1px solid rgba(255, 255, 255, 0.08)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <ClockIcon style={{ width: '1rem', height: '1rem', color: '#3b82f6' }} />
              <span style={{ fontSize: '0.6875rem', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Total Hours
              </span>
            </div>
            <p style={{ margin: 0, fontSize: '1.75rem', fontWeight: '700' }}>
              {stats?.totalHours || 0}
            </p>
          </div>
        </div>

        {/* Secondary Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '0.75rem',
          marginBottom: '1rem'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            padding: '0.625rem 0.875rem',
            background: 'rgba(245, 158, 11, 0.1)',
            borderRadius: '0.5rem',
            border: '1px solid rgba(245, 158, 11, 0.2)'
          }}>
            <span style={{ fontSize: '0.75rem', color: '#fbbf24' }}>Pending</span>
            <span style={{ fontSize: '1rem', fontWeight: '700', color: '#fbbf24' }}>
              {stats?.pendingRequests || 0}
            </span>
          </div>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            padding: '0.625rem 0.875rem',
            background: 'rgba(139, 92, 246, 0.1)',
            borderRadius: '0.5rem',
            border: '1px solid rgba(139, 92, 246, 0.2)'
          }}>
            <span style={{ fontSize: '0.75rem', color: '#a78bfa' }}>Employees</span>
            <span style={{ fontSize: '1rem', fontWeight: '700', color: '#a78bfa' }}>
              {stats?.totalEmployees || 0}
            </span>
          </div>
        </div>

        {/* Average Card */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(194, 158, 93, 0.15) 0%, rgba(194, 158, 93, 0.05) 100%)',
          borderRadius: '0.75rem',
          padding: '1rem',
          border: '1px solid rgba(194, 158, 93, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <p style={{ margin: '0 0 0.25rem', fontSize: '0.6875rem', opacity: 0.7 }}>
              Average per request
            </p>
            <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700', color: '#c29e5d' }}>
              {stats?.avgHoursPerRequest || 0} <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>hours</span>
            </p>
          </div>
          <div style={{
            width: '3rem',
            height: '3rem',
            background: 'linear-gradient(135deg, #c29e5d 0%, #d4b373 100%)',
            borderRadius: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <FireIcon style={{ width: '1.5rem', height: '1.5rem', color: 'white' }} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonthlyStatsCard;